import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Loader2, RefreshCcw, Sparkles } from 'lucide-react';
import api from '@/lib/axios';

const ESTADOS = ['', 'pendente', 'aprovado', 'rejeitado', 'pausado', 'arquivado'];
const quando = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-PT') : '—');

export default function MarketplacePartnersAdminPanel({ initialTipo = '' }) {
  const [filters, setFilters] = useState({ q: '', tipo: initialTipo, estado: '', distrito: '', plano: '' });
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [motivo, setMotivo] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/admin/marketplace/parceiros?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar parceiros.'); }
    finally { setLoading(false); }
  }, [filters]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/marketplace/parceiros/config');
      setConfig(res.data);
    } catch (_) {}
  }, []);

  useEffect(() => { load(); loadConfig(); }, [load, loadConfig]);

  const acao = async (id, payload) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await api.post(`/admin/marketplace/parceiros/${id}/moderacao`, { motivo, ...payload });
      setMsg(`Ação "${payload.acao}" aplicada.`);
      await load();
      if (detail?.id === id) openDetail(id);
      return res.data;
    } catch (e) { setErr(e.response?.data?.detail || 'Ação não aplicada.'); }
    finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin/marketplace/parceiros/${id}`);
      setDetail(res.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a abrir o perfil.'); }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      const res = await api.put('/admin/marketplace/parceiros/config', {
        associacoes_gratuitas: !!config.associacoes_gratuitas,
        validade_dias: Number(config.validade_dias),
        destaque_dias: Number(config.destaque_dias),
        limite_stand_sem_plano: Number(config.limite_stand_sem_plano),
      });
      setConfig((c) => ({ ...c, ...res.data }));
      setMsg('Configuração guardada.');
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a guardar.'); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-partners-panel">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900">
          <Building2 className="h-4 w-4 text-emerald-600" /> Stands, parceiros e associações
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-partners-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </header>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Nome, email ou slug"
          value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="admin-partners-search" />
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" value={filters.tipo}
          onChange={(e) => setFilters((f) => ({ ...f, tipo: e.target.value }))} data-testid="admin-partners-filter-type">
          <option value="">Todas as categorias</option>
          {(config?.planos ? [] : [])}
          {['stand', 'associacao', 'juridico', 'contabilidade', 'seguros', 'oficina', 'chaparia', 'pneus',
            'formacao', 'inspecao', 'reboque', 'lavagem', 'carregamento', 'viatura_substituicao',
            'financiamento', 'outros'].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" value={filters.estado}
          onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))} data-testid="admin-partners-filter-state">
          {ESTADOS.map((s) => <option key={s} value={s}>{s || 'Todos os estados'}</option>)}
        </select>
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Distrito"
          value={filters.distrito} onChange={(e) => setFilters((f) => ({ ...f, distrito: e.target.value }))} data-testid="admin-partners-filter-district" />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Plano (código)"
          value={filters.plano} onChange={(e) => setFilters((f) => ({ ...f, plano: e.target.value }))} data-testid="admin-partners-filter-plan" />
      </div>

      <input className="mt-2 w-full rounded-lg border border-amber-300 px-2 py-1.5 text-xs" placeholder="Motivo / nota (obrigatório para rejeitar ou pedir correção)"
        value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="admin-partners-reason" />

      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="admin-partners-error">{err}</p>}
      {msg && <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="admin-partners-msg">{msg}</p>}

      <div className="mt-3 overflow-x-auto">
        {loading ? (
          <p className="flex items-center gap-2 py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-xs text-slate-500" data-testid="admin-partners-empty">Sem perfis para estes filtros.</p>
        ) : (
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="py-2">Perfil</th><th>Categoria</th><th>Estado</th><th>Plano</th><th>Viaturas</th><th>Estatísticas</th><th>Validade</th><th /></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-partner-row-${p.id}`}>
                  <td className="py-2">
                    <p className="font-semibold text-slate-800">{p.nome}</p>
                    <p className="text-[10px] text-slate-400">{p.email_responsavel} · {p.distrito}</p>
                    {p.pedido_eliminacao?.estado === 'pendente' && <p className="text-[10px] font-bold text-rose-600">pediu eliminação</p>}
                  </td>
                  <td>{p.tipo_label}</td>
                  <td>{p.estado}</td>
                  <td>
                    {p.plano?.nome || '—'}
                    {p.plano?.gratuito ? ' (gratuito)' : ''}
                    {p.destaque?.ativo && <span className="ml-1 inline-flex items-center text-amber-600"><Sparkles className="h-3 w-3" /></span>}
                  </td>
                  <td>{p.entity_type === 'stand' ? `${p.viaturas ?? 0}${p.limite == null ? '' : `/${p.limite}`}` : '—'}</td>
                  <td className="text-[10px] text-slate-500">{p.estatisticas?.visualizacoes || 0} vis · {p.estatisticas?.conversas || 0} conv</td>
                  <td className="whitespace-nowrap">{quando(p.validade)}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: 'aprovar' })} className="rounded border border-emerald-300 px-1.5 py-0.5 font-semibold text-emerald-700" data-testid={`admin-partner-approve-${p.id}`}>Aprovar</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: 'rejeitar' })} className="rounded border border-rose-300 px-1.5 py-0.5 font-semibold text-rose-700" data-testid={`admin-partner-reject-${p.id}`}>Rejeitar</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: 'pedir_correcao' })} className="rounded border border-amber-300 px-1.5 py-0.5 font-semibold text-amber-700" data-testid={`admin-partner-fix-${p.id}`}>Pedir correção</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: p.estado === 'pausado' ? 'reativar' : 'pausar' })} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-partner-pause-${p.id}`}>{p.estado === 'pausado' ? 'Reativar' : 'Pausar'}</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: 'arquivar' })} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-partner-archive-${p.id}`}>Arquivar</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: p.destaque?.ativo ? 'remover_destaque' : 'destaque', destaque_distrito: p.distrito, destaque_categoria: p.entity_type })} className="rounded border border-amber-300 px-1.5 py-0.5 font-semibold text-amber-700" data-testid={`admin-partner-feature-${p.id}`}>{p.destaque?.ativo ? 'Retirar destaque' : 'Destacar'}</button>
                      <button type="button" disabled={busy} onClick={() => acao(p.id, { acao: 'gratuito', gratuito: !p.plano?.gratuito })} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-partner-free-${p.id}`}>{p.plano?.gratuito ? 'Retirar gratuidade' : 'Gratuito'}</button>
                      <button type="button" onClick={() => openDetail(p.id)} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-partner-open-${p.id}`}>Ver</button>
                    </div>
                    <div className="mt-1 flex gap-1">
                      <select onChange={(e) => e.target.value && acao(p.id, { acao: 'atribuir_plano', plano_code: e.target.value })}
                        className="rounded border border-slate-300 px-1 py-0.5 text-[10px]" defaultValue="" data-testid={`admin-partner-plan-${p.id}`}>
                        <option value="">Atribuir plano…</option>
                        {(config?.planos || []).map((pl) => <option key={pl.code} value={pl.code}>{pl.code}</option>)}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="mt-4 rounded-2xl border border-slate-300 bg-slate-50 p-3" data-testid="admin-partner-detail">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-900">{detail.nome} · {detail.tipo_label}</p>
            <button type="button" onClick={() => setDetail(null)} className="text-xs font-semibold text-slate-500 underline" data-testid="admin-partner-close">Fechar</button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            {detail.email_responsavel} · responsável: {detail.responsavel_nome} · conversas: {detail.conversas} · limite: {detail.limite === null ? 'sem limite' : detail.limite}
          </p>
          <p className="mt-2 whitespace-pre-wrap text-xs text-slate-700">{detail.descricao}</p>
          {(detail.viaturas || []).length > 0 && (
            <ul className="mt-2 space-y-0.5" data-testid="admin-partner-vehicles">
              {detail.viaturas.map((v) => <li key={v.id} className="text-[11px] text-slate-600">{v.titulo} · {v.status}</li>)}
            </ul>
          )}
          <details className="mt-2" data-testid="admin-partner-history">
            <summary className="cursor-pointer text-xs font-bold text-slate-700">Histórico ({(detail.historico || []).length})</summary>
            <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600">
              {(detail.historico || []).slice().reverse().map((h, i) => (
                <li key={i}>{quando(h.em)} · {h.acao} · {h.por || 'responsável'} {h.motivo ? `· ${h.motivo}` : ''}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {config && (
        <div className="mt-5 rounded-2xl border border-slate-200 p-3" data-testid="admin-partners-config">
          <p className="text-sm font-bold text-slate-900">Configuração</p>
          <p className="mt-1 text-[11px] text-slate-500">
            Cobranças ativas: <strong>{config.commerce_enabled ? 'sim' : 'não'}</strong> · planos no catálogo sem preço definido: {(config.planos || []).filter((p) => p.price === null).length}
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-4">
            <label className="text-[11px] text-slate-600">Validade por omissão (dias)
              <input type="number" value={config.validade_dias} onChange={(e) => setConfig((c) => ({ ...c, validade_dias: e.target.value }))}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-partners-config-validity" />
            </label>
            <label className="text-[11px] text-slate-600">Duração do destaque (dias)
              <input type="number" value={config.destaque_dias} onChange={(e) => setConfig((c) => ({ ...c, destaque_dias: e.target.value }))}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-partners-config-feature" />
            </label>
            <label className="text-[11px] text-slate-600">Limite de viaturas sem plano
              <input type="number" value={config.limite_stand_sem_plano} onChange={(e) => setConfig((c) => ({ ...c, limite_stand_sem_plano: e.target.value }))}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-partners-config-limit" />
            </label>
            <label className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-slate-600">
              <input type="checkbox" checked={!!config.associacoes_gratuitas} onChange={(e) => setConfig((c) => ({ ...c, associacoes_gratuitas: e.target.checked }))} data-testid="admin-partners-config-free" />
              Associações gratuitas no lançamento
            </label>
          </div>
          <button type="button" onClick={saveConfig} disabled={busy} className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid="admin-partners-config-save">Guardar configuração</button>
        </div>
      )}
    </section>
  );
}
